'use strict';
const { accessorials } = require('../../..');
var { fragments } = require('../fragments');
const { gql } = require('graphql-tag');
const rating = require('../rating');
const config = require('../../../config');
const { getPropValue } = require('../utilities');

var _parent = {};

var authorizations = {
  init: p => (_parent = p),

  // Don't allow any single authorization to authorize more than $500
  MAX_CHARGE_LIMIT: 500,

  // The default number of minutes the charge window is open allowing the
  // authorization to be approved. This is combined with a starting timestamp
  // to arrive at the valid_from and valid_to values on the authorization.
  DEFAULT_VALID_CHARGE_WINDOW_MINUTES: 15,

  // The default number of minutes to add on to the beginning and end of the
  // charge window when calculating using pickup time and lane duration, rather
  // than using the default overall time (DEFAULT_VALID_CHARGE_WINDOW_MINUTES)
  DEFAULT_VALID_CHARGE_BUFFER_MINUTES: 15,

  // The maximum number of attempts a card can be swiped before it is
  // set to declined without retry
  MAX_ATTEMPTS: 3,

  /**
   * Create one or more new authorizations in the database
   *
   * @param  {array} authorizations - An array of authorization record objects
   * @returns {object} A graphql mutation response
   */
  create: async objs => {
    const res = await _parent.gql.mutation(
      `
            mutation sdk_authorizations_create($objectArray: [authorizations_insert_input!]!) {
                insert_authorizations(objects: $objectArray) {
                    affected_rows
                }
            }`,
      {
        objectArray: objs,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Update an authorization record in the database
   *
   * @param  {number} id - An id for the authorization record to update
   * @param  {array} authorization - A partial authorization object with updated values
   * @returns {object} A graphql mutation response
   */
  update: async (id, obj) => {
    const res = await _parent.gql.mutation(
      `
            mutation sdk_authorizations_update($id: bigint!, $authorization: authorizations_set_input!) {
                update_authorizations(
                    where: {
                        id: {_eq: $id}
                    },
                    _set: $authorization
                ) {
                    affected_rows
                }
            }`,
      {
        id: id,
        authorization: obj,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Update an authorization record and simultaneously increment the attempts counter
   *
   * @param  {number} id - An id for the authorization record to update
   * @param  {object} authorization - A partial authorization object with updated values
   * @param  {number} inc - The number to increment the auth_attempts column with
   * @returns {object} A graphql mutation response
   */
  updateAndIncrementAuthAttempts: async (id, obj, inc = 1) => {
    console.log(`Updating Authorization ${id} and Incrementing Attempts: `, obj);
    const res = await _parent.gql.mutation(
      `
            mutation sdk_authorizations_updateAndIncrementAuthAttempts($id: bigint!, $authorization: authorizations_set_input!, $attemptsInc: smallint!) {
                update_authorizations(
                    where: {
                        id: {_eq: $id}
                    },
                    _set: $authorization,
                    _inc: {auth_attempts: $attemptsInc}
                ) {
                    affected_rows
                }
            }`,
      {
        id: id,
        authorization: obj,
        attemptsInc: inc || 1,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Update an authorization record, it's parent accessorial record, and
   * simultaneously increment the attempts counter on the authorization
   *
   * @param  {number} accessorialId - An id for the accessorial record to update
   * @param  {number} authorizationId - An id for the authorization record to update
   * @param  {array} accessorial - A partial accessorial object with updated values
   * @param  {array} authorization - A partial authorization object with updated values
   * @returns {object} A graphql mutation response
   */
  updateAccessorialAndIncrementAuthAttempts: async (
    accessorialId,
    authorizationId,
    accessorial,
    authorization,
    inc = 1
  ) => {
    const res = await _parent.gql.mutation(
      `
            mutation sdk_authorizations_updateAndIncrementAuthAttempts($accessorialId: bigint!, $authorizationId: bigint!, $accessorial: accessorials_set_input!, $authorization: authorizations_set_input!, $inc: smallint) {
                update_accessorials(
                    where: {
                        id: {_eq: $accessorialId}
                    },
                    _set: $accessorial
                ) {
                    affected_rows
                }
                update_authorizations(
                    where: {
                        id: {_eq: $authorizationId}
                    },
                    _set: $authorization,
                    _inc: {
                        auth_attempts: $inc
                    }
                ) {
                    affected_rows
                }
            }
          `,
      {
        accessorialId: accessorialId,
        authorizationId: authorizationId,
        accessorial: accessorial,
        authorization: authorization,
        inc: inc || 1,
      }
    );

    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Update an authorization record, it's parent accessorial record, but
   * do not mess with the increment of the attempts field.
   *
   * @param  {number} accessorialId - An id for the accessorial record to update
   * @param  {number} authorizationId - An id for the authorization record to update
   * @param  {array} accessorial - A partial accessorial object with updated values
   * @param  {array} authorization - A partial authorization object with updated values
   * @returns {object} A graphql mutation response
   */
  updateAccessorialAndAuthorization: async (accessorialId, authorizationId, accessorial, authorization) => {
    const res = await _parent.gql.mutation(
      `
            mutation sdk_authorizations_updateAndIncrementAuthAttempts($accessorialId: bigint!, $authorizationId: bigint!, $accessorial: accessorials_set_input!, $authorization: authorizations_set_input!) {
                update_accessorials(
                    where: {
                        id: {_eq: $accessorialId}
                    },
                    _set: $accessorial
                ) {
                    affected_rows
                }
                update_authorizations(
                    where: {
                        id: {_eq: $authorizationId}
                    },
                    _set: $authorization,
                ) {
                    affected_rows
                }
            }
          `,
      {
        accessorialId: accessorialId,
        authorizationId: authorizationId,
        accessorial: accessorial,
        authorization: authorization,
      }
    );

    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Generate a authorization object based on inputs.
   *
   * This function takes the aproach of assuming the caller wants defaults when
   * they pass incorrect information rather than throwing a bunch of errors.
   * Check that the object you got back is what you expected before using it.
   * You've been warned!
   *
   * @param {object} options
   * @param {Number} options.id - The id of the authorization
   * @param {Number} options.maxCharge - The maximum allowed to be charged on the card for this single authorization
   * @param {Timestamp} options.validFrom - A timestamp of the starting time for the window of time this authorization should allow processing to succeed
   * @param {Timestamp} options.validTo - A timestamp of the ending time for the window of time this authorization should allow processing to succeed
   * @param {Number} options.createdByUserId - The username of the user that initiated the creation of this authorization
   * @returns {object} An authorization object
   */
  buildAuthorization: (
    options = {
      id: null,
      maxCharge: 0.0,
      validFrom: null,
      validTo: null,
      createdByUserId: null,
    }
  ) => {
    let id = ['string', 'number'].indexOf(typeof options.id) > -1 ? Number(options.id) : null;
    let createdByUserId =
      ['string', 'number'].indexOf(typeof options.createdByUserId) > -1 ? options.createdByUserId : null;
    let maxCharge = ['string', 'number'].indexOf(typeof options.maxCharge) > -1 ? Number(options.maxCharge) : 0.0;
    let validFrom =
      ['string'].indexOf(typeof options.validFrom) > -1 ? options.validFrom : _parent.utilities.getLocalISOString();
    let validTo =
      ['string'].indexOf(typeof options.validTo) > -1
        ? options.validTo
        : _parent.utilities.getLocalISOString(
            _parent.utilities.addMinutes(authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES)
          );

    //Check if it exceeds the limit and if so force it to the limit
    maxCharge = maxCharge > authorizations.MAX_CHARGE_LIMIT ? authorizations.MAX_CHARGE_LIMIT : maxCharge;

    let ret = {
      max_charge: maxCharge,
    };

    //Conditionally add other optional props if passed
    if (id) ret.id = id;
    if (createdByUserId) ret.created_by = createdByUserId;
    if (validFrom) ret.valid_from = validFrom;
    if (validTo) ret.valid_to = validTo;

    return ret;
  },

  /**
   * Fetch all accessorial (with authorization) records by driver id
   *
   * This function will perform a query to find all active non-canceled
   * and incomplete moves assigned to the driver id passed in that have
   * a pending accessorial with a related authorization record.
   *
   * @param {Number} driverId - The id of the driver to lookup
   * @returns {Object} An accessorial (with authorization) record or null if not found
   */
  getPendingByDriverId: async driverId => {
    if (!driverId) throw new Error('Driver id is missing');
    if (typeof driverId !== 'number') throw new Error('Driver id is not a number');

    try {
      let start_time = _parent.utilities.getLocalISOString(_parent.utilities.addMinutes(-720, Date()));
      let end_time = _parent.utilities.getLocalISOString(_parent.utilities.addMinutes(720, Date()));
      let res = await _parent.gql.query(
        gql`
          query sdk_authorizations_getPendingByDriverId(
            $id: bigint!
            $start_time: timestamptz!
            $end_time: timestamptz!
          ) {
            accessorials(
              where: {
                status: { _eq: "pending" }
                _and: [{ createdat: { _gte: $start_time } }, { createdat: { _lte: $end_time } }]
                authorization_id: { _is_null: false }
                move: {
                  driver_id: { _eq: $id }
                  _and: [{ pickup_time: { _gte: $start_time } }, { pickup_time: { _lte: $end_time } }]
                  cancel_status: { _is_null: true }
                  active: { _eq: "1" }
                }
              }
              order_by: { move: { pickup_time: asc } }
            ) {
              id
              move_id
              code
              status
              notes
              cost
              ap_amount
              ar_amount
              createdat
              updatedat
              authorization {
                id
                vendor_auth_id
                card_id
                last4
                merchant_name
                merchant_city
                merchant_state
                merchant_postalcode
                max_charge
                driver_id
                driver_name
                auth_attempts
                last_declined_at
                last_declined_reason
                approved_at
                created_by
                created_at
                updated_at
                valid_from
                valid_to
              }
              move {
                id
                active
                status
                cancel_status
                pickup_time
                driver_name
              }
            }
          }
        `,
        { id: driverId, start_time: start_time, end_time: end_time }
      );

      if (res.success == true) {
        let accessorials = res.data;
        console.log(`Found ${res.data.length} pending accessorial(s) for Driver #${driverId} within 24 hour window`);
        if (accessorials.length === 0) throw new Error(`No accessorial (authorizations) found with id ${driverId}`);
        return accessorials;
      } else {
        console.log('failed: ', res);
        return [];
      }
    } catch (err) {
      throw err;
    }
  },

  /**
   * Fetch all accessorial (with authorization) records by move id
   *
   * @param {Number} moveId - The id of the parent move to lookup
   * @returns {Object} An accessorial (with authorization) record or null if not found
   */
  getPendingByMoveId: async moveId => {
    if (!moveId) throw new Error('Move id is missing');
    if (typeof moveId !== 'number') throw new Error('Move id is not a number');

    try {
      let res = await _parent.gql.query(
        gql`
          query sdk_authorizations_getPendingByMoveId($id: bigint!) {
            accessorials(
              where: {
                status: { _eq: "pending" }
                move: {
                  id: { _eq: $id }
                  status: { _nin: ["canceled", "failed", "", "delivery successful"] }
                  cancel_status: { _is_null: true }
                  active: { _eq: "1" }
                }
              }
            ) {
              id
              move_id
              code
              status
              notes
              cost
              ap_amount
              ar_amount
              createdat
              updatedat
              authorization {
                id
                vendor_auth_id
                card_id
                last4
                merchant_name
                merchant_city
                merchant_state
                merchant_postalcode
                max_charge
                driver_id
                driver_name
                auth_attempts
                last_declined_at
                last_declined_reason
                approved_at
                created_by
                created_at
                updated_at
                valid_from
                valid_to
              }
              move {
                id
                active
                status
                cancel_status
                pickup_time
                driver_name
                lane {
                  id
                  duration_sec
                }
              }
            }
          }
        `,
        { id: moveId }
      );

      if (res.success == true) {
        let accessorials = res.data;
        if (accessorials.length === 0) throw new Error(`No accessorial (authorizations) found with move id ${moveId}`);
        return accessorials;
      }

      return null;
    } catch (err) {
      throw err;
    }
  },

  /**
   * Fetch the most recently approved accessorial (with authorization)
   * records by vendor authorization id.
   *
   * @param {String} vendor_auth_id - The id of the vendor authorization
   * @returns {Object} An accessorial (with authorization) record or null if not found
   */
  getRecentlyApprovedByVendorAuthId: async vendorAuthId => {
    if (!vendorAuthId || vendorAuthId == '') throw new Error('Vendor auth id is missing');

    try {
      let res = await _parent.gql.query(
        gql`
          query sdk_authorizations_getRecentlyApprovedByVendorAuthId($id: String!) {
            accessorials(
              where: { status: { _eq: "approved" }, authorization: { vendor_auth_id: { _eq: $id } } }
              order_by: { authorization: { approved_at: desc } }
              limit: 1
            ) {
              id
              move_id
              code
              status
              notes
              cost
              ap_amount
              ar_amount
              createdat
              updatedat
              authorization {
                id
                vendor_auth_id
                card_id
                last4
                merchant_name
                merchant_city
                merchant_state
                merchant_postalcode
                max_charge
                driver_id
                driver_name
                auth_attempts
                last_declined_at
                last_declined_reason
                approved_at
                created_by
                created_at
                updated_at
                valid_from
                valid_to
              }
              move {
                id
                createdat
                customer_id
                lane {
                  id
                  distance_miles
                  delivery {
                    id
                    region_id
                  }
                  destination_location_id
                  pickup {
                    id
                    region_id
                  }
                  origin_location_id
                }
                pickup_time
                ready_by
                status
              }
            }
          }
        `,
        { id: vendorAuthId }
      );

      if (res.success == true) {
        let accessorials = res.data;
        if (accessorials.length === 0)
          throw new Error(`No accessorial (authorizations) found by vendor auth id ${vendorAuthId}`);
        return accessorials[0]; //Only return the first index since we are fetching one record
      }

      return null;
    } catch (err) {
      throw err;
    }
  },

  /**
   * @typedef {Object} AuthorizationResponse
   * @property {Boolean} approved - True if the authorization was approved
   * @property {Number} amount - The amount the authorization is approved up to
   * @property {String} message - Any message about the authorization attempt
   * @property {Object} accessorial - The accessorial the transaction paired with
   */

  /**
   * Process a new credit card authorization request from 3rd party
   *
   * This function will check for an applicable authorization record
   * and apply the credit card authorization information to it recording
   * the attempt as either successful or not in the authorization record
   * and returning with the authorized amount that can be subsequently
   * returned to the calling vendor.
   *
   * @param {Number} driverId - The id of the driver attempting the authorization
   * @param {object} options
   * @param {Number} options.vendorAuthId - The id of the vendor authorization
   * @param {Number} options.cardId - The id of the vendor credit card
   * @param {Number} options.last4 - The last 4 digits of the credit card number
   * @param {Number} options.merchantCategory - The category string of the merchant code
   * @param {Number} options.merchantName - The name of the place the card is being swiped at
   * @param {Number} options.merchantCity - The city of the place the card is being swiped at
   * @param {Number} options.merchantState - The state of the place the card is being swiped at
   * @param {Number} options.merchantPostalcode - The postal code of the place the card is being swiped at
   * @param {Number} options.driverName - The name of the driver the card is assigned to (firstname + lastname)
   * @param {Number} options.amount - The dollar amount being requested for authorization
   * @returns {AuthorizationResponse} An object with approved boolean and amount for the approved spend maximum
   */
  isApproved: async (
    driverId,
    options = {
      vendorAuthId: '',
      cardId: '',
      last4: '',
      merchantCategory: '',
      merchantName: '',
      merchantCity: '',
      merchantState: '',
      merchantPostalcode: '',
      driverName: '',
      amount: 0.0,
    },
    timer = 0
  ) => {
    const {
      vendorAuthId,
      cardId,
      last4,
      merchantCategory,
      merchantName,
      merchantCity,
      merchantState,
      merchantPostalcode,
      driverName,
      amount,
    } = options;

    // const timer = timer || Date.now()

    // if (typeof driverId !== 'number') {
    //   try {
    //     driverId = Number(driverId);
    //   } catch (e) { }
    // }

    if (typeof driverId !== 'number') throw new Error('Driver id is not a number');

    //Using the driver ID, find all accessorials on active moves that
    // have status of pending with a parent move that is neither canceled
    // nor completed successfully or otherwise (failed for example).

    const accessorials = await authorizations.getPendingByDriverId(driverId);
    console.log(
      ` ⏰ 1) Queried ${accessorials.length} accessorials. Function has taken ${Date.now() - timer} milliseconds`
    );

    //The above call is allowed to throw an exception if the driverId
    // is invalid or if no matching records are found. It is allowed
    // to throw up and cause this function to throw it as well to
    // it's caller. If that happens, we will not make it past this
    // point in the code.

    let matchedAccessorial;

    //Identify the accessorial/authorization this swipe could be for
    // by looping over each found accessorial and applying some more
    // criteria to each one. They should be sorted already by the
    // pickup_times with the earliest first from the GQL query sort.
    for (let i = 0; i < accessorials.length; i++) {
      const accessorial = accessorials[i];
      const { move = {}, authorization = {} } = accessorial;

      const now = new Date();
      const validFromDate = new Date(authorization.valid_from);
      const validToDate = new Date(authorization.valid_to);

      //Authorizations have a small window of time that they are
      // valid for. If the current time is not within that range,
      // then we need to consider the authorization invalid.

      if (validToDate < validFromDate || validToDate.getTime() === validFromDate.getTime()) {
        //The valid window of this authorization is not valid. The
        // valid_from date must be before the valid_to date. This
        // typically should not occurr since both the UI and the SDK
        // will have checks in place to prevent it.
        console.log(
          `Expiring Accessorial #${accessorial.id}, Authorization Window Invalid: ${
            validToDate.getHours() + ':' + validToDate.getMinutes()
          } - ${validFromDate.getHours() + ':' + validFromDate.getMinutes()}`
        );

        //Because it is invalid we can safely expire it and write
        // the exire reason appropriately for the dispatcher to
        // be able to diagnose why the card swipe failed.
        await authorizations.expire({
          accessorialId: accessorial.id,
          authorizationId: authorization.id,
          reason: accessorial.notes + ', auto-expired (invalid window)',
        });

        continue;
      }

      if (validToDate < now) {
        //This authorization has already expired since we have already
        // passed the valid window of time that it can be used
        console.log(
          `Expiring Accessorial #${accessorial.id}, Card Swiped too Late: ${
            validToDate.getHours() + ':' + validToDate.getMinutes()
          } < ${now.getHours() + ':' + now.getMinutes()}`
        );

        await authorizations.expire({
          accessorialId: accessorial.id,
          authorizationId: authorization.id,
          reason: accessorial.notes + ', auto-expired (invalid window)',
        });

        continue;
      }

      if (now < validFromDate) {
        //This authorization is outside of the valid window
        console.log(
          `Declining Accessorial #${accessorial.id} with Retry, Card Swiped too Early: ${
            now.getHours() + ':' + now.getMinutes()
          } < ${validFromDate.getHours() + ':' + validFromDate.getMinutes()}`
        );

        //We decline here in order to give insight into the
        // driver's attempt to use the card at the pump when they
        // shouldn't be (not to catch them, but to correct them)

        const reason = `Card swiped too early (${now.getHours() + ':' + now.getMinutes()} < ${
          validFromDate.getHours() + ':' + validFromDate.getMinutes() + ')'
        }`;

        //Retry max attempts is monitored within this function and will
        // fail automatically if the max is exceeded

        await authorizations.declineWithRetry({
          accessorialId: accessorial.id,
          authorizationId: authorization.id,
          vendorAuthId,
          cardId,
          last4,
          merchantName,
          merchantCity,
          merchantState,
          merchantPostalcode,
          driverId,
          driverName,
          reason,
          authAttempts: authorization.auth_attempts,
          existingNotes: authorization.notes,
        });

        continue;
      }
      console.log(` ⏰ 2) Validated time. function has taken ${Date.now() - timer} milliseconds`);

      //Check if the move is in the right status
      const invalidStatusList = [
        null,
        '',
        'canceled',
        'failed',
        'delivery successful',
        'dispatched',
        'pickup started',
        'pickup arrived',
        'pickup successful',
      ];
      const validStatusList = ['delivery started', 'delivery arrived'];

      if (!validStatusList.includes(move.status)) {
        //This move is not in a valid status so we need to record
        // a decline on the authorization and move to the next

        let reasonStatus = move.status;
        if (move.status === null) {
          reasonStatus = 'unplanned';
        }

        console.log(`Declining Accessorial #${accessorial.id} with Retry, Move Status Invalid:`, reasonStatus);

        await authorizations.declineWithRetry({
          accessorialId: accessorial.id,
          authorizationId: authorization.id,
          vendorAuthId,
          cardId,
          last4,
          merchantName,
          merchantCity,
          merchantState,
          merchantPostalcode,
          driverId,
          driverName,
          reason: `Invalid move status: ${reasonStatus}`,
          authAttempts: authorization.auth_attempts,
          existingNotes: accessorial.notes,
        });

        continue;
      }

      console.log(` ⏰ 3) Validated Move status. function has taken ${Date.now() - timer} milliseconds`);

      //Verify the merchant code based on the valid codes by
      // accessorial type
      switch (accessorial.code) {
        case _parent.accessorials.types.FUEL.code:
          //Check that for fuel type authorizations that the
          // merchant category or MCC is aligned with swiping
          // at the pump only and not inside the gas station.
          // 'automated_fuel_dispensers', MCC: 5542
          if (merchantCategory !== 'automated_fuel_dispensers') {
            console.log(
              `Declining Accessorial #${accessorial.id} with Retry, Fuel Accessorial not swiped at Automated Fuel Dispenser:`,
              merchantCategory
            );

            await authorizations.declineWithRetry({
              accessorialId: accessorial.id,
              authorizationId: authorization.id,
              vendorAuthId,
              cardId,
              last4,
              merchantName,
              merchantCity,
              merchantState,
              merchantPostalcode,
              driverId,
              driverName,
              reason: `Card not swiped at gas pump: ${merchantName}, ${merchantCategory}`,
              authAttempts: authorization.auth_attempts,
              existingNotes: accessorial.notes,
            });

            continue;
          }

          break;

        case _parent.accessorials.types.TOW.code:
          //At the time of writing, we only support using the
          // driver cards for fuel. In the future if we expand
          // to towing or other move related expenses we must
          // modify this switch to check the MCC for those

          // if (merchantCategory !== '???') continue
          console.log('Tow Accessorial');
          continue;
        // break;

        default:
          console.log('Invalid Accessorial Type');
          continue;
        // break;
      }

      console.log(` ⏰ 4) Validated Merchant code. function has taken ${Date.now() - timer} milliseconds`);

      //If we made it all the way down here, then we have a valid
      // authorization that should be approved so we can exit the
      // loop and do just that
      matchedAccessorial = accessorial;
      console.log(
        `Accessorial #${matchedAccessorial.id} has been validated! Card was swiped within valid window, Move was in valid status, and Fuel Accessorial was swiped at fuel pump`
      );
      break;
    }

    if (matchedAccessorial) {
      //Removed the approval from here in favor of putting it outside
      // after we confirm the approval was received correctly by
      // the Stripe system

      const { authorization = {} } = matchedAccessorial;
      console.log(` ⏰ 5) Accessorial ready to be approved. function has taken ${Date.now() - timer} milliseconds`);
      return { approved: true, amount: authorization.max_charge, message: null, accessorial: matchedAccessorial };
    } else {
      //We were unable to find a suitable pre-authorization accessorial
      // to match this charge attempt up with. We must simply decline
      // it, but we should consider a way to reflect that on the
      // driver as an attempt to swipe without a move.

      //TODO is there a place to record a card decline when no
      // matched accessorial authorization was found?

      // Can use the Stripe API / Website

      return { approved: false, amount: 0, message: `No accessorial authorizations found for driver ${driverId}` };
    }
  },

  /**
   * Handle a card transaction update from 3rd party
   *
   * This function will accept the new final charge amount from the vendor
   * after the transaction is completed. This is important because often
   * the initial authorization amount is merely a hold being placed for
   * the potential funds to be charged. Once the transaction is completed,
   * the final amount charged is known and we need to update our records.
   *
   * @param {Number} vendorAuthId - The id of the vendor authorization
   * @param {Number} amount - The dollar amount hte vendor is telling us was finally charged
   * @returns {Object} { approved: false, amount: 0 } An object with approved boolean and amount for the approved spend maximum
   */
  handleCardAuthorizationUpdate: async stripeEvent => {
    //Using the vendor auth ID, find the most recently approved
    // accessorial authorization
    const {
      data: {
        object: {
          id: vendorAuthId,
          amount = 0,
          status,
          merchant_data: {
            category: merchantCategory,
            name: merchantName,
            city: merchantCity,
            state: merchantState,
            postal_code: merchantPostalcode,
          } = {},
          verification_data = {},
        } = {},
      } = {},
    } = stripeEvent || {};

    const chargeAmount = amount / 100; //Comes over without decimal

    const accessorial = await authorizations.getRecentlyApprovedByVendorAuthId(vendorAuthId);
    console.log(`Found recently approved Accessorial #${accessorial.id}`);
    const { authorization } = accessorial;
    const { move } = accessorial;

    //Let's reflect that an update come through on the authorization
    let authorizationForUpdate = {
      updated_at: 'now()',
    };

    let accessorialForUpdate;

    //Recalculate the accessorial amounts based on the type of
    // accessorial it is
    switch (accessorial.code) {
      case _parent.accessorials.types.FUEL.code:
        // Rob 20221116 - Check if this is a reversed transaction then decline if so
        if (status === 'reversed') {
          const {
            address_line1_check = '',
            address_postal_code_check = '',
            cvc_check = '',
            expiry_check = '',
          } = verification_data;

          console.log(`Charges reversed at the pump: ${JSON.stringify(verification_data, null, 2)}`);

          let reason = `Charges reversed at the pump:`;
          if (address_postal_code_check === 'mismatch') reason += ' zip mismatch';
          if (cvc_check === 'mismatch') reason += ' CVC (CVV) mismatch';
          if (expiry_check === 'mismatch') reason += ' expiration mismatch';
          if (address_line1_check === 'mismatch') reason += ' address 1 mismatch';

          // Get driver info if available
          let driver = {};
          try {
            //Get cardHolderId to look up driverId
            const cardholder = getPropValue(stripeEvent, 'data.object.card.cardholder');

            //Get the driver from the swiped card
            let driverId = cardholder.metadata ? Number(cardholder.metadata.driver_id) : null;

            const { individual = {} } = cardholder;
            driver = {
              id: driverId,
              first_name: individual.first_name || '',
              last_name: individual.last_name || '',
            };
          } catch (error) {
            console.log('Failed to get driver id and name from stripe event');
          }

          await authorizations.decline({
            accessorialId: accessorial.id,
            authorizationId: authorization.id,
            merchantName,
            merchantCity,
            merchantState,
            merchantPostalcode,
            driverId: driver.id,
            driverName: `${driver.first_name} ${driver.last_name}`,
            reason,
            authAttempts: authorization.auth_attempts,
            existingNotes: accessorial.notes,
          });

          return { approved: false, amount: 0, message: reason };
        }

        // Rob 20221116 - If we made it past the above early exit for reversed transactions
        // then we proceed into the following code assuming it's a closed transaction and we
        // are basically just updating the final charges from the fuel pump (what was actually
        // charged at the pump vs what was authorized)

        //TODO: Is there a way to get a more accurate refueling duration
        // the card authorization activity timestamps?

        //Using market level configurations for Fuel Multipier and Pay Per Minute
        const driverRate = await _parent.rating.driverPay(config.server, config.secret).getDriverRate(move);
        const { per_minute_rate } = driverRate;

        const customerConfig = await _parent.configs.getCustomerConfig(move.customer_id);
        const { fuel_multiplier } = customerConfig;

        //Now that we know the full price, we need to recalculate the AP
        let tempAcc = {};

        console.log(
          `New Cost from Update Event: $${chargeAmount}, Market Driver Pay Per Minute: $${per_minute_rate}, Customer Fuel Multiplier ${fuel_multiplier}`
        );

        //Crash work-around
        try {
          tempAcc = _parent.accessorials.buildFuelAccessorial({
            moveId: accessorial.move_id,
            cost: chargeAmount,
            fuelMultiplier: fuel_multiplier,
            calcDriverTimePay: true,
            payPerMinute: per_minute_rate,
            durationSeconds: _parent.accessorials.DEFAULT_DURATION_SECONDS,
            costBearer: _parent.accessorials.costBearers.COMPANY.code,
            notes: accessorial.notes ? accessorial.notes : '',
          });

          console.log(
            `Successfully rebuilt Accessorial using updated cost and market values: ${JSON.stringify(tempAcc, null, 2)}`
          );
        } catch (e) {
          console.log(`Error rebuilding accessorial with updated cost`);
          tempAcc = {
            move_id: accessorial.move_id,
            code: _parent.accessorials.types.FUEL.code,
            status: _parent.accessorials.statusCodes.APPROVED.code,
            ap_amount: 0,
            ar_amount: 0,
            cost: 0,
            notes: accessorial.notes ? `${accessorial.notes} | Fuel Accessorial build error` : 'Fuel Accessorial build error',
          };
        }

        //Check to make sure we aren't losing data here if something
        // goes wrong recalculating the fees. Check for 0's coming back
        // and abort the update if they're found
        if (chargeAmount === 0) {
          console.log(`Cost = ${chargeAmount}, Failed to recalculate AP and AR amounts`);
          accessorialForUpdate = {
            cost: chargeAmount,
            notes: tempAcc.notes,
            updatedat: 'now()',
          };
        } else {
          accessorialForUpdate = {
            cost: chargeAmount,
            ap_amount: tempAcc.ap_amount,
            ar_amount: tempAcc.ar_amount,
            notes: tempAcc.notes,
            updatedat: 'now()',
          };
        }

        break;

      case _parent.accessorials.types.TOW.code:
        //At the time of writing, we only support using the
        // driver cards for fuel. In the future if we expand
        // to towing or other move related expenses we must
        // modify this switch to check the MCC for those

        //SO that we don't lose the updated charge amount, simply write it
        // to the cost for now, but we will need to doa more thoughtful
        // design for this accessorial type in the future.
        accessorialForUpdate = {
          cost: chargeAmount,
          notes: tempAcc.notes + `, auth amount update: updated cost only (${accessorial.code})`,
        };

        break;

      default:
        //If we got here then there something unknown happening but we
        // should not lose the update charged amount from the vendor
        accessorialForUpdate = {
          cost: chargeAmount,
          notes: tempAcc.notes + `, auth amount update: updated cost only (unknown code)`,
        };
        break;
    }

    const accRes = await _parent.accessorials.update(accessorial.id, accessorialForUpdate);
    const authRes = await authorizations.update(authorization.id, authorizationForUpdate);

    const { success: accSuccess, message: accMessage } = accRes;
    const { success: authSuccess, message: authMessage } = authRes;

    if (!accSuccess) {
      return { approved: false, amount: chargeAmount, message: `Accessorial update unsuccessful: ${accMessage}` };
    } else if (!authSuccess) {
      return { approved: false, amount: chargeAmount, message: `Authorization update unsuccessful: ${authMessage}` };
    } else {
      console.log(`Accessorial Update Successful: ${JSON.stringify(accessorialForUpdate, null, 2)}`);
      return { approved: true, amount: chargeAmount, message: `Updated Accessorial and Authorization` };
    }
  },

  /**
   * Approve an accessorial with authorization.
   *
   * This function will update the accessorial status to approved and will also
   * record details about the transaction on the authorization child record.
   *
   * @param {object} options
   * @param {Number} options.accessorialId - The id of the authorization to update
   * @param {Number} options.authorizationId - The id of the authorization to update. If this is not passed, then the accessorialId is required.
   * @returns {Boolean} True if successful in updating the records
   */
  approve: async (
    options = {
      accessorialId: 0,
      authorizationId: 0,
      vendorAuthId: '',
      cardId: '',
      last4: '',
      merchantName: '',
      merchantCity: '',
      merchantState: '',
      merchantPostalcode: '',
      driverId: 0,
      driverName: '',
      amount: 0.0,
    }
  ) => {
    const { accessorialId, authorizationId, reason } = options;

    if (!authorizationId) throw new Error('An authorizationId is required');
    if (!accessorialId) throw new Error('An accessorialId is required');

    let accessorial = {
      status: _parent.accessorials.statusCodes.APPROVED.code,
      updatedat: 'now()',
    };

    //Append the optional values
    let authorization = {
      approved_at: 'now()',
      updated_at: 'now()',
    };

    //Append optional values
    if (options.vendorAuthId) authorization.vendor_auth_id = options.vendorAuthId;
    if (options.cardId) authorization.card_id = options.cardId;
    if (options.last4) authorization.last4 = options.last4;
    if (options.merchantName) authorization.merchant_name = options.merchantName;
    if (options.merchantCity) authorization.merchant_city = options.merchantCity;
    if (options.merchantState) authorization.merchant_state = options.merchantState;
    if (options.merchantPostalcode) authorization.merchant_postalcode = options.merchantPostalcode;
    if (options.driverId) authorization.driver_id = options.driverId;
    if (options.driverName) authorization.driver_name = options.driverName;

    //Update the status of the parent accessorial at the same time as the authorization
    const updateRes = await authorizations.updateAccessorialAndIncrementAuthAttempts(
      accessorialId,
      authorizationId,
      accessorial,
      authorization,
      1
    );
    const { data = {} } = updateRes;
    return data.affected_rows > 0;
  },

  /**
   * Expire an accessorial with authorization.
   *
   * This function will update the accessorial status to expired and will also
   * record details about the transaction on the authorization child record.
   *
   * @param {object} options
   * @param {Number} options.accessorialId - The id of the authorization to update
   * @param {Number} options.authorizationId - The id of the authorization to update. If this is not passed, then the accessorialId is required.
   * @returns {Boolean} True if successful in updating the records
   */
  expire: async (
    options = {
      accessorialId: 0,
      authorizationId: 0,
      reason: '',
    }
  ) => {
    const { accessorialId, authorizationId, reason } = options;

    if (!authorizationId) throw new Error('An authorizationId is required');
    if (!accessorialId) throw new Error('An accessorialId is required');

    let accessorial = {
      status: _parent.accessorials.statusCodes.EXPIRED.code,
      updatedat: 'now()',
      notes: reason,
    };

    let authorization = {
      approved_at: null,
      updated_at: 'now()',
      valid_from: null,
      valid_to: null,
    };

    //Update the status of the parent accessorial at the same time as the authorization
    const updateRes = await authorizations.updateAccessorialAndIncrementAuthAttempts(
      accessorialId,
      authorizationId,
      accessorial,
      authorization,
      1
    );
  },

  /**
   * Update the approval window of an accessorial with authorization.
   *
   * This function will update the valid_from and valid_to on the authorization
   * record to whatever number of minutes past now or an override passed in.
   *
   * @param {object} options
   * @param {Number} options.accessorialId - The id of the authorization to update
   * @param {Number} options.authorizationId - The id of the authorization to update. If this is not passed, then the accessorialId is required.
   * @param {Number} options.minutesPastStart - The number of minutes past now to use (or past the optional startTime param if its passed)
   * @param {Timestamp} options.startTime - An instance of a JS Date object
   */
  updateValidWindow: async (
    options = {
      accessorialId: 0,
      authorizationId: 0,
      minutesPastStart: authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES,
      startTime: new Date(),
    }
  ) => {
    const { accessorialId, authorizationId, minutesPastStart, startTime } = options;

    let mins = authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES;
    let validFrom = _parent.utilities.getLocalISOString();

    if (!authorizationId) throw new Error('An authorizationId is required');
    if (!accessorialId) throw new Error('An accessorialId is required');

    if (minutesPastStart) mins = minutesPastStart;
    if (startTime) validFrom = _parent.utilities.getLocalISOString(startTime);

    if (mins < 0) mins = authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES;

    let validTo = _parent.utilities.getLocalISOString(_parent.utilities.addMinutes(mins, startTime));

    let accessorial = {
      updatedat: 'now()',
    };

    let authorization = {
      approved_at: null,
      updated_at: 'now()',
      valid_from: validFrom,
      valid_to: validTo,
    };

    //Update the status of the parent accessorial at the same time as the authorization
    const updateRes = await authorizations.updateAccessorialAndAuthorization(
      accessorialId,
      authorizationId,
      accessorial,
      authorization
    );
  },

  /**
   * Update the approval window of all valid authorizations
   * related to the provided move id
   *
   * @param {Number} moveId - The id of the move to update
   * @param {object} options
   * @param {Number} options.minutesPastStart - The number of minutes past now to use (or past the optional startTime param if its passed)
   * @param {Date} options.startTime - A JS Date object of the starting time for the window
   */
  updateValidWindowsByMoveId: async (
    moveId,
    options = {
      minutesPastStart: undefined,
      startTime: new Date(),
    }
  ) => {
    const { minutesPastStart, startTime } = options;

    const accs = await authorizations.getPendingByMoveId(moveId);

    if (!accs) return;

    for (let i = 0; i < accs.length; i++) {
      const acc = accs[i];
      const { authorization = {}, move = {} } = acc;
      const { lane = {} } = move;
      const { duration_sec = 0 } = lane;

      //A local valiable to hold the resolved duration of seconds
      let durMinutes = 0;

      try {
        //If we did not override the duration then we should use defaults
        if (minutesPastStart === undefined) {
          //Try to use the duration seconds from the lane but if
          // for any reason that is not available fall back to the
          // hardcoded module default constant
          if (duration_sec > 0) {
            durMinutes = Math.floor(Number(duration_sec) / 60);
          } else {
            durMinutes = authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES;
          }
        } else {
          //If minutesPastStart was passed we need to ensure its a valid number. We
          // will accept a number passed in as a string too since data often comes
          // from the result of a GQL query with string types for everything
          if (['string', 'number'].indexOf(typeof minutesPastStart) > -1) {
            durMinutes = Number(minutesPastStart);
          } else {
            durMinutes = authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES;
          }
        }
      } catch (e) {
        durMinutes = authorizations.DEFAULT_VALID_CHARGE_WINDOW_MINUTES;
      }

      let dt = new Date();

      //If we did not override the start time then we should use defaults
      if (startTime === undefined) {
        dt = new Date();
      } else {
        dt = startTime instanceof Date && !isNaN(startTime) ? startTime : new Date();
      }

      await authorizations.updateValidWindow({
        accessorialId: acc.id,
        authorizationId: authorization.id,
        minutesPastStart: durMinutes,
        startTime: dt,
      });
    }
  },

  /**
   * Decline an accessorial authorization without retry capability.
   *
   * This function will record an attempt on the authorization, update
   * the last declined at timestamp and reason, then change the status
   * of the parent accessorial to declined.
   *
   * @param {object} options
   * @param {Number} options.accessorialId - The id of the authorization to update
   * @param {Number} options.authorizationId - The id of the authorization to update. If this is not passed, then the accessorialId is required.
   * @returns {Boolean} True if successful in updating the records
   */
  decline: async (
    options = {
      accessorialId: 0,
      authorizationId: 0,
      vendorAuthId: '',
      cardId: '',
      last4: '',
      merchantName: '',
      merchantCity: '',
      merchantState: '',
      merchantPostalcode: '',
      driverId: 0,
      driverName: '',
      reason: '',
      note: '',
    }
  ) => {
    const {
      accessorialId,
      authorizationId,
      reason,
      note,
      vendorAuthId,
      cardId,
      last4,
      merchantName,
      merchantCity,
      merchantState,
      merchantPostalcode,
      driverId,
      driverName,
    } = options;

    console.log('Declining Accessorial:', options);

    if (!authorizationId) throw new Error('An authorizationId is required');
    if (!accessorialId) throw new Error('An accessorialId is required');

    let accessorial = {
      status: _parent.accessorials.statusCodes.DECLINED.code,
      updatedat: 'now()',
      notes: note,
    };

    let authorization = {
      last_declined_at: 'now()',
      last_declined_reason: reason,
      approved_at: null,
      updated_at: 'now()',
    };

    //Append optional values
    if (vendorAuthId) authorization.vendor_auth_id = vendorAuthId;
    if (cardId) authorization.card_id = cardId;
    if (last4) authorization.last4 = last4;
    if (merchantName) authorization.merchant_name = merchantName;
    if (merchantCity) authorization.merchant_city = merchantCity;
    if (merchantState) authorization.merchant_state = merchantState;
    if (merchantPostalcode) authorization.merchant_postalcode = merchantPostalcode;
    if (driverId) authorization.driver_id = driverId;
    if (driverName) authorization.driver_name = driverName;

    //Update the status of the parent accessorial at the same time as the authorization
    const updateRes = await authorizations.updateAccessorialAndIncrementAuthAttempts(
      accessorialId,
      authorizationId,
      accessorial,
      authorization,
      1
    );
  },

  /**
   * Decline an accessorial authorization with the ability to retry again.
   *
   * This function will record an attempt on the authorization, update
   * the last declined at timestamp and reason, but leave the status
   * of the parent accessorial as pending.
   *
   * @param {object} options
   * @param {Number} options.accessorialId - The id of the authorization to update
   * @param {Number} options.authorizationId - The id of the authorization to update. If this is not passed, then the accessorialId is required.
   * @returns {Boolean} True if successful in updating the records
   */
  declineWithRetry: async (
    options = {
      accessorialId: 0,
      authorizationId: 0,
      vendorAuthId: '',
      cardId: '',
      last4: '',
      merchantName: '',
      merchantCity: '',
      merchantState: '',
      merchantPostalcode: '',
      driverId: 0,
      driverName: '',
      reason: '',
      authAttempts: 0,
      existingNotes: '',
    }
  ) => {
    const {
      accessorialId,
      authorizationId,
      reason,
      vendorAuthId,
      cardId,
      last4,
      merchantName,
      merchantCity,
      merchantState,
      merchantPostalcode,
      driverId,
      driverName,
      authAttempts,
      existingNotes,
    } = options;
    console.log('Auth Attempts:', authAttempts, 'Max Attempts:', authorizations.MAX_ATTEMPTS);
    if (!authorizationId) throw new Error('An authorizationId is required');
    if (!accessorialId) throw new Error('An accessorialId is required');

    if (authAttempts >= authorizations.MAX_ATTEMPTS) {
      console.log('Max Attempts Reached, Declining Accessorial');
      return authorizations.decline({
        accessorialId: accessorialId,
        authorizationId: authorizationId,
        vendorAuthId,
        cardId,
        last4,
        merchantName,
        merchantCity,
        merchantState,
        merchantPostalcode,
        driverId,
        driverName,
        note: existingNotes + `, max attempts exceeded: ${authorizations.MAX_ATTEMPTS}`,
        reason,
      });
    } else {
      console.log('Max Attempts Not Yet Reached, Incrementing Auth Attempts without Decline');
    }

    let authorization = {
      last_declined_at: 'now()',
      last_declined_reason: reason,
      approved_at: null,
      updated_at: 'now()',
    };

    //Append optional values
    if (vendorAuthId) authorization.vendor_auth_id = vendorAuthId;
    if (cardId) authorization.card_id = cardId;
    if (last4) authorization.last4 = last4;
    if (merchantName) authorization.merchant_name = merchantName;
    if (merchantCity) authorization.merchant_city = merchantCity;
    if (merchantState) authorization.merchant_state = merchantState;
    if (merchantPostalcode) authorization.merchant_postalcode = merchantPostalcode;
    if (driverId) authorization.driver_id = driverId;
    if (driverName) authorization.driver_name = driverName;

    const updateRes = await authorizations.updateAndIncrementAuthAttempts(authorizationId, authorization, 1);
  },
};

module.exports = authorizations;

/* Authorization table columns with notes

    id - bigint, primary key, unique, default: nextval('authorizations_id_seq'::regclass)
        Insert: autoincrement
        Update: This should never be updated

    vendor_auth_id - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec
        Notes: This is the authorization id from the vendor (stripe in this case). When
        an authorization attempt is made and this record is found to match it during
        approval check in our webhook, this column is updated with that value.
        Subsequently if that fails then the next authorization attempt will override
        this value. This is a trade-off being made that I'm not entirely sure I like
        but it's to cut down on the duplication of data from stripe into our DB. We
        may decide in the future to come back to this.

    card_id - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    last4 - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    merchant_name - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    merchant_city - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    merchant_state - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    merchant_postalcode - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    max_charge - numeric, default: 0
        Insert: The max allowable charge for the authorized spend
        Update: This would only be updatable before the transaction is approved
                by an admin that wishes to correct a mistake they made in creating
                the authorization for the wrong amount.

    driver_id - bigint, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    driver_name - text, nullable
        Insert: null
        Update: This is updated when a card swipe occurs that matches to this rec

    auth_attempts - smallint, default: 0
        Insert: null
        Update: This is incremented automatically for each authorization attempt

    last_declined_at - timestamp without time zone, nullable
        Insert: null
        Update: This is updated with the current UTC timestamp if an authorization
                is declined.

    last_declined_reason - text, nullable
        Insert: null
        Update: This is updated with the decline reason if an authorization is
                declined.

    created_by - text, nullable
        Insert: user id of the frontend user that created the record
        Update: This should never be updated

    created_at - timestamp with time zone, nullable, default: now()
        Insert: null to take column dafault of now()
        Update: This should never be updated

    updated_at - timestamp with time zone, nullable, default: now()
        Insert: null to take column dafault of now()
        Update: current UTC timestamp

    updated_at - timestamp with time zone, nullable, default: now()
        Insert: null to take column dafault of now()
        Update: current UTC timestamp

    valid_from - timestamp with time zone, nullable, default: null
        Insert: null
        Update: User controlled start of valid time window. A credit
                card authorization (swipe) attempt will be declined
                if the system time when the swipe occurs is not
                withing th evalid from - valid_to time range

    valid_to - timestamp with time zone, nullable, default: null
        Insert: null
        Update: User controlled start of valid time window. A credit
                card authorization (swipe) attempt will be declined
                if the system time when the swipe occurs is not
                withing th evalid from - valid_to time range

*/
